import { useTemplateImageUpload } from '@/features/campaigns/hooks/useTemplateImageUpload';
import { RefreshIconOutline, TrashIcon, UploadCloudIcon } from '@/lib';
import TipTapEditor from '@/modules/TiptapEditor/TipTapEditor';
import { ArrayFieldTemplateProps, ObjectFieldTemplateProps, WidgetProps } from '@rjsf/utils';
import { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100';

const textareaClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const errorClass = 'mt-1 text-xs font-medium text-red-600';

type EnumOption = { value: unknown; label: string };

type UploadedFileMeta = { name: string; size: number };

/** True when enum options look like a WhatsApp header type (None / Text / Image). */
export const isHeaderTypeEnum = (enumOptions?: EnumOption[]): boolean => {
  if (!enumOptions?.length) return false;
  const values = new Set(enumOptions.map((option) => String(option.value).toUpperCase()));
  return values.has('NONE') && values.has('TEXT') && values.has('IMAGE');
};

export const formatTemplateImageSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
};

export const fileNameFromImageUrl = (url: string): string => {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
    return name || 'image';
  } catch {
    return 'image';
  }
};

export const ObjectFieldTemplate: FC<ObjectFieldTemplateProps> = (props) => {
  const { title, description, properties } = props;

  return (
    <div className='space-y-6'>
      {title && (
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{title}</h2>
          {description && <p className='mt-1 text-sm text-gray-500'>{props.description}</p>}
        </div>
      )}

      <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
        {properties?.map((property) => {
          const fullWidthFields = [
            'header',
            'template_header_text',
            'header_examples',
            'header_image_url',
            'content',
            'template_body_text',
            'template_body_html',
            'body_examples',
            'footer',
            'template_footer_text',
          ];

          const isFullWidth = fullWidthFields.includes(property.name);

          return (
            <div key={property.name} className={isFullWidth ? 'md:col-span-2' : ''}>
              {property.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const TextWidget: FC<WidgetProps> = (props) => {
  return (
    <input
      id={props.id}
      type='text'
      value={props.value || ''}
      placeholder={props.placeholder}
      disabled={props.disabled || props.readonly}
      required={props.required}
      className={inputClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    />
  );
};

export const TextareaWidget: FC<WidgetProps> = (props) => {
  const rows = props.options.rows || 5;

  return (
    <textarea
      id={props.id}
      value={props.value || ''}
      placeholder={props.placeholder}
      disabled={props.disabled || props.readonly}
      required={props.required}
      rows={Number(rows)}
      className={textareaClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    />
  );
};

export const SelectWidget: FC<WidgetProps> = (props) => {
  const enumOptions = (props.options.enumOptions ?? []) as EnumOption[];
  const disabled = props.disabled || props.readonly;

  if (isHeaderTypeEnum(enumOptions)) {
    return (
      <div
        id={props.id}
        role='tablist'
        aria-required={props.required || undefined}
        className='wa-seg'
      >
        {enumOptions.map((option) => {
          const selected = props.value === option.value;

          return (
            <button
              key={String(option.value)}
              type='button'
              role='tab'
              aria-selected={selected}
              disabled={disabled}
              className='wa-seg-tab rounded'
              onClick={() => props.onChange(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <select
      id={props.id}
      value={props.value || ''}
      disabled={disabled}
      required={props.required}
      className={inputClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    >
      <option value=''>Select...</option>

      {enumOptions.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export const RadioWidget: FC<WidgetProps> = (props) => {
  return (
    <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
      {props.options.enumOptions?.map((option: any) => {
        const checked = props.value === option.value;

        return (
          <label
            key={option.value}
            className={`!flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-3 text-sm transition ${
              checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <input
              type='radio'
              name={props.id}
              value={option.value}
              checked={checked}
              disabled={props.disabled || props.readonly}
              className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
              onChange={() => props.onChange(option.value)}
            />
            <span className='font-bold'>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const TipTapEditorWidget: FC<WidgetProps> = (props) => (
  <TipTapEditor
    id={props.id}
    value={props.value ?? ''}
    onChange={props.onChange}
    disabled={props.disabled || props.readonly}
  />
);

const DEFAULT_IMAGE_ACCEPT = ['image/png', 'image/jpeg'];

export const ImageUploadWidget: FC<WidgetProps> = (props) => {
  const { t } = useTranslation('campaign');
  const { upload, isUploading } = useTemplateImageUpload();
  const [error, setError] = useState<string>();
  const [fileMeta, setFileMeta] = useState<UploadedFileMeta>();
  const accept = (props.options['accept'] as string[] | undefined) ?? DEFAULT_IMAGE_ACCEPT;
  const disabled = props.disabled || props.readonly;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    multiple: false,
    disabled: disabled || isUploading,
    // Rejected files are still passed to upload(), which reports why they are refused.
    onDrop: async (accepted, rejected) => {
      const file = accepted[0] ?? rejected[0]?.file;
      if (!file) return;
      setError(undefined);
      try {
        const url = await upload(file, accept);
        setFileMeta({ name: file.name, size: file.size });
        props.onChange(url);
      } catch (e) {
        setError((e as Error).message);
      }
    },
  });

  const clearImage = () => {
    setFileMeta(undefined);
    setError(undefined);
    props.onChange(undefined);
  };

  const displayName = fileMeta?.name ?? (props.value ? fileNameFromImageUrl(String(props.value)) : '');
  const displaySize = fileMeta ? formatTemplateImageSize(fileMeta.size) : undefined;

  return (
    <div className='space-y-2'>
      <p className='text-sm text-gray-900'>{t('templateImages.hint')}</p>
      <input id={props.id} {...getInputProps()} />

      {props.value ? (
        <div className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white-100 p-3'>
          <img
            src={props.value}
            alt=''
            className='h-14 w-14 shrink-0 rounded-md border border-gray-100 object-cover'
          />
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-semibold text-gray-900' title={displayName}>
              {displayName}
            </div>
            {displaySize ? <div className='mt-0.5 text-xs text-gray-500'>{displaySize}</div> : null}
          </div>
          {!disabled && (
            <div className='flex shrink-0 items-center gap-2'>
              <button
                type='button'
                className='wa-file-action inline-flex items-center gap-1.5 rounded-lg'
                onClick={() => open()}
                disabled={isUploading}
              >
                <RefreshIconOutline className='size-4' />
                {t('templateImages.replace')}
              </button>
              <button
                type='button'
                aria-label={t('templateImages.remove')}
                className='wa-file-action wa-file-action--icon inline-flex items-center justify-center rounded-lg'
                onClick={clearImage}
                disabled={isUploading}
              >
                <TrashIcon className='size-4' />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          {...getRootProps({
            className: `flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-10 text-center text-sm transition ${
              disabled
                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                : 'cursor-pointer border-gray-300 text-gray-600 hover:border-blue-500 hover:bg-blue-50'
            } ${isDragActive ? 'border-blue-500 bg-blue-50' : ''}`,
          })}
        >
          {isUploading ? (
            <div className='font-medium'>{t('templateImages.uploading')}</div>
          ) : (
            <>
              <UploadCloudIcon className='size-8 text-gray-400' />
              <div className='text-sm text-gray-600'>
                <Trans
                  t={t}
                  i18nKey='templateImages.dropOrBrowse'
                  components={{
                    browse: <span className='font-medium text-blue-600' />,
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
      {error && <div className={errorClass}>{error}</div>}
    </div>
  );
};

export const ArrayFieldTemplate: FC<ArrayFieldTemplateProps> = (props) => {
  return (
    <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
      <div className='mb-3 flex items-start justify-between gap-4'>
        <div>
          {props.title && <h3 className='text-sm font-semibold text-gray-900'>{props.title}</h3>}
          {props.schema.description && <p className='mt-1 text-xs text-gray-500'>{props.schema.description}</p>}
        </div>

        {props.canAdd && (
          <button
            type='button'
            onClick={props.onAddClick}
            className='rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-700'
          >
            Add example
          </button>
        )}
      </div>

      <div className='space-y-3'>
        {props.items.map((item: any) => (
          <div key={item.key} className='flex items-start gap-3 rounded-lg border border-gray-200 bg-white-100 p-3'>
            <div className='flex-1'>{item.children}</div>

            {item.hasRemove && (
              <button
                type='button'
                onClick={item.onDropIndexClick(item.index)}
                className='rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50'
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const templates = {
  ObjectFieldTemplate,
  ArrayFieldTemplate,
};

export const widgets = {
  TextWidget,
  TextareaWidget,
  SelectWidget,
  RadioWidget,
  TipTapEditor: TipTapEditorWidget,
  ImageUploadWidget,
};
